import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { InputBaseType } from '@rd/form-builder/types';
import { GiftCardHelper } from '@rd/helpers/gift-card.helper';
import {
  CashPointsRatio,
  CashPointsSliderConfig,
  CheckoutTermsListItem,
  CurrencyFormat,
  GiftCard,
  GiftCardCategory,
  GiftCardCategoryOption,
  GiftCardDenomination,
  GiftCardOrderItemData,
  GiftCardQueryParams,
  GiftCardSort,
  GiftCardSortOption,
  GiftCardType,
  GiftCardTypeOption,
  Order,
  OrderItemType,
  SuccessButton,
  SuccessButtonGroup,
} from '@rd/types';
import { NAME_VALIDATION_REGEX } from '@rd/utils/regex.utils';

type checkoutPageText = 'preSubmitButtonText' | 'prePhysicalGCSubmitButtonText';

export type GcCheckoutPageTextMap = Record<
  checkoutPageText,
  { translationKey: string; translationParams: Record<string, any> } | null
>;

type successPageText =
  | 'contactUsMessage'
  | 'notifyMessage'
  | 'manageGiftCardMessage';

export type GcSuccessPageTextMap = Record<
  successPageText,
  { translationKey: string; translationParams?: Record<string, any> } | null
>;

export const defaultGiftCardQueryParams: GiftCardQueryParams = {
  category: GiftCardCategory.All,
  searchByBrand: '',
  type: GiftCardType.All,
  sort: GiftCardSort.Alphabet,
};

@Injectable()
export class GiftCardSettingService {
  cashPointsRatio: CashPointsRatio = CashPointsRatio.FullPoints;
  checkoutTermsListCount = 3;
  giftCardCategoryOptions: Array<string> = Object.values(GiftCardCategory);

  /*
  ====================================
    Listing page-only settings
  ====================================
  */

  giftCardQueryParams: GiftCardQueryParams = defaultGiftCardQueryParams;
  listingPageLowestDenominationCurrencyFormat: CurrencyFormat = 'symbol';
  listingPageHighestDenominationCurrencyFormat: CurrencyFormat = 'symbol';

  typeOptions: GiftCardTypeOption<any>[] = [
    { name: 'gift_card.filter_modal.digital', value: GiftCardType.Digital },
  ];
  // WLs default to have empty category options.
  categoryOptions: GiftCardCategoryOption<any>[] = [];
  sortOptions: GiftCardSortOption<any>[] = [];

  shouldRenderListingPageBreadcrumb = false;

  // Setting for enabling category pills bar on GC listing.
  shouldRenderCategoryFilter = true;

  // Setting for enabling Filter options such as category / type filters.
  shouldEnableFilter = false;
  // Setting for enabling Sort option.
  shouldEnableSort = false;
  // Setting for enabling Type option.
  shouldEnableGiftCardTypeFilter = false;

  shouldEnablePhysicalGiftCard = false;

  /*
  ====================================
    Review page settings
  ====================================
  */

  /*
  ====================================
    Success page-only settings
  ====================================
  */

  successPageCurrencyFormat = 'symbol';

  // Should show quantity - by default, it's hidden, for Anz it's shown, even if quantity is 1
  shouldShowQuantityInSuccessPage = false;

  private defaultSuccessPageText: GcSuccessPageTextMap = {
    contactUsMessage: {
      translationKey: 'gift_card.success.contact_us',
    },
    notifyMessage: {
      translationKey: 'gift_card.success.will_notify',
    },
    manageGiftCardMessage: null,
  };

  protected successPageTextOverrides: Partial<GcSuccessPageTextMap> = {};

  get successPageText(): any {
    return {
      ...this.defaultSuccessPageText,
      ...this.successPageTextOverrides,
    };
  }

  shouldRequireAgreeTnc = false;

  /*
  ====================================
    Checkout/details page-only settings
  ====================================
  */

  shouldPillsOverflowOnMobile = false;
  populateRecipientDetailsFromStore = false;
  disableEditingRecipientDetailsForm = false;
  shouldHaveArrowOnGcTncs = false;
  gcTncsArrowIconPath = 'arrow-right';
  shouldDisplayQuantityCounter = false;

  // Setting for enabling Display types
  shouldEnableDisplayTypes = false;

  cashPointsSliderConfig: CashPointsSliderConfig | null = null;

  defaultGiftCardDisplayType: GiftCardType = GiftCardType.Digital;

  // checkoutPageText: This would allow us to add text to the template
  // if a key was set up in the GcSettingService. Check AnzGcSettingService.
  // If a tenant was to add text in a location that is not in the standard checkout/details page,
  // then they should add the relevant key in checkoutPageTextOverrides.

  private defaultCheckoutPageText: GcCheckoutPageTextMap = {
    preSubmitButtonText: null,
    prePhysicalGCSubmitButtonText: null,
  };

  protected checkoutPageTextOverrides: Partial<GcCheckoutPageTextMap> = {};

  get checkoutPageText(): GcCheckoutPageTextMap {
    return {
      ...this.defaultCheckoutPageText,
      ...this.checkoutPageTextOverrides,
    };
  }

  private defaultCheckoutPageDigitalGcFormFields: Record<
    string,
    InputBaseType
  > = {
    recipientFirstName: {
      className: 'recipient-first-name',
      key: 'recipientFirstName',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_first_name',
        emptyErrorLabel: 'gift_card.checkout.error.recipient_first_name_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_first_name_invalid_pattern',
      },
      syncValidators: [Validators.pattern(NAME_VALIDATION_REGEX)],
    },
    recipientLastName: {
      className: 'recipient-last-name',
      key: 'recipientLastName',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_last_name',
        emptyErrorLabel: 'gift_card.checkout.error.recipient_last_name_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_last_name_invalid_pattern',
      },
      syncValidators: [Validators.pattern(NAME_VALIDATION_REGEX)],
    },
    recipientMobileNumber: {
      className: 'recipient-mobile-number',
      key: 'recipientMobileNumber',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_mobile_number',
        emptyErrorLabel:
          'gift_card.checkout.error.recipient_mobile_number_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_mobile_number_invalid_pattern',
      },
    },
    recipientEmail: {
      className: 'recipient-email',
      key: 'recipientEmail',
      labelTranslation: {
        label: 'gift_card.checkout.label.recipient_email',
        emptyErrorLabel: 'gift_card.checkout.error.recipient_email_empty',
        invalidPatternErrorLabel:
          'gift_card.checkout.error.recipient_email_invalid_pattern',
      },
    },
  };

  protected checkoutPageDigitalGcFormFieldsOverrides: Partial<
    Record<string, InputBaseType>
  > = {};

  get checkoutPageDigitalGiftCardFormFields(): Record<
    string,
    InputBaseType | undefined
  > {
    return {
      ...this.defaultCheckoutPageDigitalGcFormFields,
      ...this.checkoutPageDigitalGcFormFieldsOverrides,
    };
  }
  /*
  ====================================
    Review page-only settings
  ====================================
  */

  shouldShowQuantityInReviewPage = false;

  constructor(protected giftCardHelper: GiftCardHelper) {}

  successButtons(order: Order): SuccessButtonGroup {
    const giftCardOrderItem = order.orderItems?.find(
      (orderItem) => orderItem.type === OrderItemType.GiftCardOrderItem,
    );
    const giftCardOrderItemAttributes = giftCardOrderItem?.attributes;

    if (!giftCardOrderItemAttributes) {
      throw new Error('giftCardOrderItemAttributes is not defined');
    }

    const orderItemData =
      giftCardOrderItemAttributes.data as GiftCardOrderItemData;

    const toPointActivityButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'gift_card.success.to_points_transaction',
      buttonTheme: 'primary',
      redirectURL: '/points_activity',
    };

    // Only fulfilled orders have fulfillmentData attribute
    const downloadGiftCardButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'gift_card.success.view_gift_card_voucher',
      buttonTheme: 'primary',
      redirectURL:
        order.fulfillmentStatus === 'fulfilled' &&
        'fulfillmentUrl' in orderItemData.fulfillmentData[0]
          ? orderItemData.fulfillmentData[0].fulfillmentUrl
          : null,
    };

    const toHomePageButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'gift_card.success.redeem_another_gift_card',
      buttonTheme: 'secondary',
      redirectURL: '/',
    };

    return {
      confirmed: [
        { ...toPointActivityButton, buttonTheme: 'secondary' },
        downloadGiftCardButton,
      ],
      pending: [toHomePageButton, toPointActivityButton],
      failed: [toHomePageButton, toPointActivityButton],
    };
  }

  checkoutTermsListItems(params: {
    giftCard: GiftCard;
    selectedDenomination: GiftCardDenomination;
  }): CheckoutTermsListItem[] {
    const { giftCard, selectedDenomination } = params;
    return [
      {
        iconSymbol: 'icon-deliver',
        iconTheme: 'primary',
        translationKey: this.giftCardHelper.deliveryMethodInfo(giftCard),
      },
      {
        iconSymbol: 'icon-location-pin',
        iconTheme: 'primary',
        translationKey: 'gift_card.checkout.info.redeemable_in_country_only',
        translationParams: { country: 'Australia' },
      },
      {
        iconSymbol: 'icon-validity',
        iconTheme: 'primary',
        translationKey:
          this.giftCardHelper.validityDuration(selectedDenomination)
            .translationKey,
        translationParams:
          this.giftCardHelper.validityDuration(selectedDenomination)
            .translationParams,
      },
    ];
  }
}
